import React, { useState, useEffect } from 'react';
import { Alert, Card, Row, Col, Tag, Button, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  FileImageOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  ThunderboltOutlined,
  SwapOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  FileGifOutlined,
  CompressOutlined,
  HeartOutlined,
  HeartFilled,
} from '@ant-design/icons';
import {
  isFeatureExpected,
  saveLocalFeedback,
  submitFeedbackToServer,
  getAllFeatureExpectedCounts,
} from '../../utils/feedbackUtils';
import { buildImgCloudRoutePath } from '../../utils/appRouting';
import './index.css';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getImageProcessingToolVisitCounts,
  ImageProcessingToolKey,
  ImageProcessingToolVisitCounts,
  reportImageProcessingToolVisit,
} from '../../services/imageProcessingToolVisits';

const { Title, Paragraph } = Typography;

interface ToolConfig {
  key: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  tags: Array<{ text: string; color: string }>;
  buttonText: string;
  buttonIcon: React.ReactNode;
  gradient: string;
  iconBgColor: string;
  comingSoon: boolean;
  visitToolKey?: ImageProcessingToolKey;
  routePath?: string;
  externalUrl?: string;
}

const ImageProcessing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expectedCounts, setExpectedCounts] = useState<Record<string, number>>({});
  const [localExpected, setLocalExpected] = useState<Record<string, boolean>>({});
  const [loadingTools, setLoadingTools] = useState<Set<string>>(new Set());
  const [visitCounts, setVisitCounts] = useState<ImageProcessingToolVisitCounts>({});

  // 初始化本地期待状态和期待人数
  useEffect(() => {
    const toolKeys: string[] = [
      'watermark',
      'ai-photo',
      'white-background',
      'compress',
      'format-convert',
      'video-to-gif',
      'video-compressor',
      'enhance',
    ];
    const localState: Record<string, boolean> = {};
    toolKeys.forEach((key) => {
      localState[key] = isFeatureExpected(key);
    });
    setLocalExpected(localState);

    // 从服务器加载期待人数
    loadExpectedCounts();
    loadVisitCounts();
  }, []);

  // 加载期待人数
  const loadExpectedCounts = async () => {
    try {
      const counts = await getAllFeatureExpectedCounts();
      setExpectedCounts(counts);
    } catch (error) {
      console.error('Failed to load expected counts:', error);
      // 失败时使用空对象，不显示人数
    }
  };

  const loadVisitCounts = async () => {
    try {
      setVisitCounts(await getImageProcessingToolVisitCounts());
    } catch {
      // Visit counts are optional and must not affect the existing cards.
    }
  };

  // 处理期待按钮点击
  const handleExpectClick = async (toolKey: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡

    const isCurrentlyExpected = localExpected[toolKey] || false;
    const newExpectedState = !isCurrentlyExpected;

    // 更新本地状态
    setLocalExpected((prev) => ({
      ...prev,
      [toolKey]: newExpectedState,
    }));

    // 保存到本地存储
    saveLocalFeedback(toolKey, newExpectedState);

    // 显示提示信息
    if (newExpectedState) {
      message.success(t('imageProcessing.feedbackThanks'));
    } else {
      message.info(t('imageProcessing.expectCancelled'));
    }

    // 可选：提交到服务器
    setLoadingTools((prev) => new Set(prev).add(toolKey));
    try {
      const result = await submitFeedbackToServer(toolKey, newExpectedState);
      if (result.success && result.count !== undefined) {
        setExpectedCounts((prev) => ({
          ...prev,
          [toolKey]: result.count || 0,
        }));
      }
    } catch (error) {
      // 错误已在 submitFeedbackToServer 中处理
    } finally {
      setLoadingTools((prev) => {
        const next = new Set(prev);
        next.delete(toolKey);
        return next;
      });
    }
  };

  const tools: ToolConfig[] = [
    // 已开启的功能放在前面
    {
      key: 'compress',
      icon: <ThunderboltOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      title: t('imageProcessing.tools.compress.title'),
      description: t('imageProcessing.tools.compress.description'),
      tags: [
        { text: t('imageProcessing.tools.compress.tag1'), color: 'orange' },
        { text: t('imageProcessing.tools.compress.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.compress.tag3'), color: 'red' },
      ],
      buttonText: t('imageProcessing.tools.compress.buttonText'),
      buttonIcon: <ThunderboltOutlined />,
      gradient: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
      iconBgColor: '#fa8c16',
      comingSoon: false,
      visitToolKey: 'compress',
      routePath: '/image-compress',
    },
    {
      key: 'format-convert',
      icon: <SwapOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: t('imageProcessing.tools.formatConvert.title'),
      description: t('imageProcessing.tools.formatConvert.description'),
      tags: [
        { text: t('imageProcessing.tools.formatConvert.tag1'), color: 'purple' },
        { text: t('imageProcessing.tools.formatConvert.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.formatConvert.tag3'), color: 'red' },
      ],
      buttonText: t('imageProcessing.tools.formatConvert.buttonText'),
      buttonIcon: <SwapOutlined />,
      gradient: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
      iconBgColor: '#722ed1',
      comingSoon: false,
      visitToolKey: 'format-convert',
      routePath: '/format-convert',
    },
    {
      key: 'video-to-gif',
      icon: <VideoCameraOutlined style={{ fontSize: '32px', color: '#08979c' }} />,
      title: t('imageProcessing.tools.videoToGif.title'),
      description: t('imageProcessing.tools.videoToGif.description'),
      tags: [
        { text: t('imageProcessing.tools.videoToGif.tag2'), color: 'gold' },
        { text: t('imageProcessing.tools.videoToGif.tag3'), color: 'green' },
      ],
      buttonText: t('imageProcessing.tools.videoToGif.buttonText'),
      buttonIcon: <VideoCameraOutlined />,
      gradient: 'linear-gradient(135deg, #08979c 0%, #13c2c2 55%, #ffc53d 100%)',
      iconBgColor: '#08979c',
      comingSoon: false,
      visitToolKey: 'video-to-gif',
      routePath: '/video-to-gif',
    },
    {
      key: 'video-compressor',
      icon: <CompressOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />,
      title: t('imageProcessing.tools.videoCompressor.title'),
      description: t('imageProcessing.tools.videoCompressor.description'),
      tags: [
        { text: t('imageProcessing.tools.videoCompressor.tag1'), color: 'cyan' },
        { text: t('imageProcessing.tools.videoCompressor.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.videoCompressor.tag3'), color: 'blue' },
      ],
      buttonText: t('imageProcessing.tools.videoCompressor.buttonText'),
      buttonIcon: <CompressOutlined />,
      gradient: 'linear-gradient(135deg, #0f766e 0%, #13c2c2 58%, #60a5fa 100%)',
      iconBgColor: '#13c2c2',
      comingSoon: false,
      visitToolKey: 'video-compressor',
      routePath: '/video-compressor',
    },
    {
      key: 'gif-maker',
      icon: <FileGifOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />,
      title: t('imageProcessing.tools.gifMaker.title'),
      description: t('imageProcessing.tools.gifMaker.description'),
      tags: [
        { text: t('imageProcessing.tools.gifMaker.tag1'), color: 'magenta' },
        { text: t('imageProcessing.tools.gifMaker.tag2'), color: 'volcano' },
        { text: t('imageProcessing.tools.gifMaker.tag3'), color: 'green' },
      ],
      buttonText: t('imageProcessing.tools.gifMaker.buttonText'),
      buttonIcon: <FileGifOutlined />,
      gradient: 'linear-gradient(135deg, #eb2f96 0%, #ff7a45 55%, #ffc53d 100%)',
      iconBgColor: '#eb2f96',
      comingSoon: false,
      visitToolKey: 'gif-maker',
      externalUrl: 'https://gif.imgbed.cn/',
    },
    // 即将推出的功能放在后面
    {
      key: 'watermark',
      icon: <FileImageOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: t('imageProcessing.tools.watermark.title'),
      description: t('imageProcessing.tools.watermark.description'),
      tags: [
        { text: t('imageProcessing.tools.watermark.tag1'), color: 'blue' },
        { text: t('imageProcessing.tools.watermark.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.watermark.tag3'), color: 'cyan' },
      ],
      buttonText: t('imageProcessing.tools.watermark.buttonText'),
      buttonIcon: <FileImageOutlined />,
      gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
      iconBgColor: '#0f766e',
      comingSoon: false,
      visitToolKey: 'watermark',
      routePath: '/image-watermark',
    },
    {
      key: 'ai-photo',
      icon: <IdcardOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: t('imageProcessing.tools.aiPhoto.title'),
      description: t('imageProcessing.tools.aiPhoto.description'),
      tags: [
        { text: t('imageProcessing.tools.aiPhoto.tag1'), color: 'blue' },
        { text: t('imageProcessing.tools.aiPhoto.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.aiPhoto.tag3'), color: 'default' },
      ],
      buttonText: t('imageProcessing.tools.aiPhoto.buttonText'),
      buttonIcon: <PictureOutlined />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #48c6ef 100%)',
      iconBgColor: '#1890ff',
      comingSoon: true,
    },
    {
      key: 'white-background',
      icon: <ShoppingOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      title: t('imageProcessing.tools.whiteBackground.title'),
      description: t('imageProcessing.tools.whiteBackground.description'),
      tags: [
        { text: t('imageProcessing.tools.whiteBackground.tag1'), color: 'orange' },
        { text: t('imageProcessing.tools.whiteBackground.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.whiteBackground.tag3'), color: 'default' },
      ],
      buttonText: t('imageProcessing.tools.whiteBackground.buttonText'),
      buttonIcon: <ShoppingOutlined />,
      gradient: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
      iconBgColor: '#fa8c16',
      comingSoon: true,
    },
    {
      key: 'enhance',
      icon: <PictureOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: t('imageProcessing.tools.enhance.title'),
      description: t('imageProcessing.tools.enhance.description'),
      tags: [
        { text: t('imageProcessing.tools.enhance.tag1'), color: 'purple' },
        { text: t('imageProcessing.tools.enhance.tag2'), color: 'cyan' },
        { text: t('imageProcessing.tools.enhance.tag3'), color: 'default' },
      ],
      buttonText: t('imageProcessing.tools.enhance.buttonText'),
      buttonIcon: <PictureOutlined />,
      gradient: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
      iconBgColor: '#722ed1',
      comingSoon: true,
    },
  ];

  const isInteractiveTarget = (target: EventTarget | null) => (
    target instanceof Element &&
    Boolean(target.closest('a, button, input, textarea, select, [role="button"]'))
  );

  const openTool = (tool: ToolConfig) => {
    if (tool.visitToolKey) {
      reportImageProcessingToolVisit(tool.visitToolKey);
    }

    if (tool.routePath) {
      navigate(tool.routePath);
      return;
    }

    if (tool.externalUrl) {
      window.location.href = tool.externalUrl;
    }
  };

  const handleToolCardClick = (event: React.MouseEvent<HTMLDivElement>, tool: ToolConfig) => {
    if (
      (!tool.routePath && !tool.externalUrl) ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      isInteractiveTarget(event.target)
    ) {
      return;
    }

    openTool(tool);
  };

  const handleToolCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, tool: ToolConfig) => {
    if (
      (!tool.routePath && !tool.externalUrl) ||
      event.defaultPrevented ||
      isInteractiveTarget(event.target) ||
      (event.key !== 'Enter' && event.key !== ' ')
    ) {
      return;
    }

    event.preventDefault();
    openTool(tool);
  };

  const handleToolLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, tool: ToolConfig) => {
    if (
      event.defaultPrevented ||
      event.button !== 0
    ) {
      return;
    }

    if (tool.visitToolKey) {
      reportImageProcessingToolVisit(tool.visitToolKey);
    }

    if (
      !tool.routePath ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();
    navigate(tool.routePath);
  };

  const handleToolLinkAuxClick = (event: React.MouseEvent<HTMLAnchorElement>, tool: ToolConfig) => {
    if (event.defaultPrevented || event.button !== 1 || !tool.visitToolKey) {
      return;
    }

    reportImageProcessingToolVisit(tool.visitToolKey);
  };

  return (
    <div className="image-processing-page">
      <Alert
        message={t('imageProcessing.bannerText')}
        type="info"
        showIcon
        style={{ marginBottom: '12px' }}
      />

      {/* 工具卡片区域 */}
      <div className="processing-content">
        <Row gutter={[24, 24]}>
          {tools.map((tool) => {
            const toolHref = tool.externalUrl || (tool.routePath ? buildImgCloudRoutePath(tool.routePath) : undefined);

            return (
              <Col xs={24} sm={12} lg={8} key={tool.key}>
                <div
                  className={`tool-card-hover-area ${toolHref ? 'tool-card-clickable' : ''}`}
                  role={toolHref ? 'link' : undefined}
                  tabIndex={toolHref ? 0 : undefined}
                  aria-label={toolHref ? tool.title : undefined}
                  onClick={(event) => handleToolCardClick(event, tool)}
                  onKeyDown={(event) => handleToolCardKeyDown(event, tool)}
                >
                  <Card
                    className="tool-card"
                    hoverable={!tool.comingSoon}
                    style={{
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div className="tool-card-header">
                      <div
                        className="tool-icon-wrapper"
                        style={{ backgroundColor: `${tool.iconBgColor}15` }}
                      >
                        {tool.icon}
                      </div>
                    </div>
                    <div className="tool-card-body">
                      <div className="tool-title-row">
                        <Title level={4} className="tool-title">
                          {tool.title}
                        </Title>
                        {tool.comingSoon && (
                          <Button
                            type="text"
                            size="small"
                            icon={localExpected[tool.key] ? <HeartFilled style={{ fontSize: 12 }} /> : <HeartOutlined style={{ fontSize: 12 }} />}
                            loading={loadingTools.has(tool.key)}
                            onClick={(e) => handleExpectClick(tool.key, e)}
                            className={`expect-button ${localExpected[tool.key] ? 'expected' : ''}`}
                            title={localExpected[tool.key] ? t('imageProcessing.expectedFeature') : t('imageProcessing.expectFeature')}
                            style={{ fontSize: 12, height: 24, padding: '0 8px' }}
                          >
                            {localExpected[tool.key] ? t('imageProcessing.expected') : t('imageProcessing.expect')}
                            {expectedCounts[tool.key] !== undefined && expectedCounts[tool.key] > 0 && (
                              <span className="expect-count">({expectedCounts[tool.key]})</span>
                            )}
                          </Button>
                        )}
                      </div>
                      <Paragraph className="tool-description" ellipsis={{ rows: 2 }}>
                        {tool.description}
                      </Paragraph>
                      <div className="tool-tags">
                        {tool.tags.map((tag, index) => (
                          <Tag key={index} color={tag.color} style={{ marginBottom: '8px' }}>
                            {tag.text}
                          </Tag>
                        ))}
                        {tool.visitToolKey && !tool.comingSoon && visitCounts[tool.visitToolKey] ? (
                          <Tag color="blue" style={{ marginBottom: '8px' }}>
                            {t('imageProcessing.visitCount', { count: visitCounts[tool.visitToolKey] })}
                          </Tag>
                        ) : null}
                      </div>
                    </div>
                    <div className="tool-card-footer">
                      {toolHref ? (
                        <a
                          className="tool-action-link"
                          href={toolHref}
                          onClick={(event) => handleToolLinkClick(event, tool)}
                          onAuxClick={(event) => handleToolLinkAuxClick(event, tool)}
                          style={{ background: tool.gradient }}
                        >
                          {tool.buttonIcon}
                          <span>{tool.buttonText}</span>
                        </a>
                      ) : (
                        <span
                          className="tool-action-link tool-action-link-disabled"
                          aria-disabled="true"
                          style={{ background: '#d9d9d9' }}
                        >
                          {tool.buttonIcon}
                          <span>{tool.buttonText}</span>
                        </span>
                      )}
                    </div>
                  </Card>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </div>
  );
};

export default ImageProcessing;
